export interface ContentsEntry {
  id: string;
  text: string;
  depth: 2 | 3;
}

interface MdNode {
  type: string;
  depth?: number;
  value?: string;
  children?: MdNode[];
  data?: { hProperties?: Record<string, unknown> };
}

export function slugify(text: string): string {
  const slug = text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s-]+/g, '-');
  return slug || 'section';
}

function textOf(node: MdNode): string {
  return node.value ?? (node.children ?? []).map(textOf).join('');
}

/**
 * A remark plugin for react-markdown: gives each `##` and `###` heading an id and reports them, in document order,
 * to `onHeadings`. Both come from the same markdown tree, so the contents list always matches the rendered ids.
 */
export function remarkContents(onHeadings: (entries: ContentsEntry[]) => void) {
  return (tree: MdNode) => {
    const entries: ContentsEntry[] = [];
    const used = new Set<string>();
    const walk = (node: MdNode) => {
      if (node.type === 'heading' && (node.depth === 2 || node.depth === 3)) {
        const text = textOf(node).trim();
        if (text) {
          const base = slugify(text);
          // Checked against every id handed out, so "Steps", "Steps" and "Steps-2" can't collide.
          let id = base;
          for (let n = 2; used.has(id); n += 1) id = `${base}-${n}`;
          used.add(id);
          node.data = { ...node.data, hProperties: { ...node.data?.hProperties, id } };
          entries.push({ id, text, depth: node.depth });
        }
      }
      node.children?.forEach(walk);
    };
    walk(tree);
    onHeadings(entries);
  };
}

export function sameContents(a: ContentsEntry[], b: ContentsEntry[]): boolean {
  return a.length === b.length && a.every((e, i) => e.id === b[i].id && e.text === b[i].text && e.depth === b[i].depth);
}
